// PlanChangedSheet.tsx — Canvas Weekly Commitments · Ship 6 (May 13, 2026)
//
// Rep-facing modal triggered from the home CommitmentCard. Lets the rep
// propose a different 7×3 plan for the current week. Submits as a
// commitment_change_requests row (manager Accept/Reject in the audit board).
//
// Reps can NOT edit the locked weekly_commitments row directly — RLS blocks
// it. This sheet just creates a REQUEST. The manager's audit board surfaces
// it with Accept/Reject buttons; Accept overwrites the blocks, Reject closes
// the request with an optional note.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import {
  type BlocksJson,
  type DayLabel,
  type BlockLabel,
  DAY_LABELS,
  BLOCK_LABELS,
  isCommitted,
  weekRangeLabel,
} from "../../lib/commitmentMath";
import { notifyOnPlanChange } from "../../lib/notifyOnPlanChange";

interface PlanChangedSheetProps {
  open: boolean;
  onClose: () => void;
  commitmentId: string | null;
  currentBlocks: BlocksJson;
  weekStart: string;
  repId: string;
  repName: string;
  onSubmitted: () => void;
}

const REASONS = ["family", "sick", "weather", "other"] as const;
type Reason = (typeof REASONS)[number];

const REASON_LABEL: Record<Reason, string> = {
  family: "Family / personal",
  sick: "Sick / unwell",
  weather: "Weather",
  other: "Other",
};

export default function PlanChangedSheet({
  open, onClose,
  commitmentId, currentBlocks, weekStart,
  repId, repName, onSubmitted,
}: PlanChangedSheetProps) {
  const [proposed, setProposed] = useState<BlocksJson>(currentBlocks);
  const [reason, setReason] = useState<Reason>("family");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hasPending, setHasPending] = useState<boolean>(false);

  // Reset on open
  useEffect(() => {
    if (open) {
      setProposed(currentBlocks);
      setReason("family");
      setNote("");
      setErr(null);
    }
  }, [open, currentBlocks]);

  // Check if user already has a pending request for this commitment
  useEffect(() => {
    if (!open || !commitmentId) { setHasPending(false); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("commitment_change_requests")
        .select("id")
        .eq("commitment_id", commitmentId)
        .eq("user_id", repId)
        .eq("status", "pending")
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      setHasPending(!!data);
    })();
    return () => { cancelled = true; };
  }, [open, commitmentId, repId]);

  function toggle(day: DayLabel, block: BlockLabel) {
    setProposed((prev) => {
      const dayBlocks = { ...(prev[day] ?? {}) };
      dayBlocks[block] = !(dayBlocks[block] === true);
      return { ...prev, [day]: dayBlocks };
    });
  }

  // Count of cells where proposed differs from current
  const diff = useMemo(() => {
    let added = 0, removed = 0;
    for (const d of DAY_LABELS) {
      for (const b of BLOCK_LABELS) {
        const was = isCommitted(currentBlocks, d, b);
        const now = isCommitted(proposed, d, b);
        if (was && !now) removed++;
        if (!was && now) added++;
      }
    }
    return { added, removed, total: added + removed };
  }, [currentBlocks, proposed]);

  async function submit() {
    if (!commitmentId) { setErr("No commitment to change — talk to your TL/ASM."); return; }
    if (diff.total === 0) { setErr("Toggle at least one block — nothing to change yet."); return; }
    if (hasPending) { setErr("You already have a pending request for this week. Wait for it to resolve."); return; }

    setSubmitting(true);
    setErr(null);
    const { data, error } = await supabase
      .from("commitment_change_requests")
      .insert({
        commitment_id: commitmentId,
        user_id: repId,
        proposed_blocks: proposed,
        reason,
        note: note.trim() || null,
      })
      .select("id")
      .single();

    if (error) {
      setSubmitting(false);
      setErr(error.message);
      return;
    }

    // Fire notifications (fire-and-forget — don't block on the SMS)
    notifyOnPlanChange({
      changeRequestId: data.id,
      repId,
      repName,
      reason,
      note: note.trim() || null,
      blocksChangedCount: diff.total,
    }).catch((e) => console.warn("[PlanChangedSheet] notify failed:", e));

    setSubmitting(false);
    onSubmitted();
    onClose();
  }

  // ESC key closes the sheet
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 sm:items-center" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-changed-title"
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:rounded-2xl"
      >
        {/* Header */}
        <div className="border-b border-slate-200 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id="plan-changed-title" className="text-base font-bold text-slate-900">📅 Plan changed?</h2>
              <p className="mt-0.5 text-[12px] text-slate-600">
                Toggle the blocks you need to change for the week of <strong>{weekRangeLabel(weekStart)}</strong>.
                Your TL/ASM gets a notification and can Accept or Reject from the audit board.
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          {hasPending ? (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900">
              <strong>You already have a pending request</strong> for this week — wait for your TL/ASM to act on it before sending another.
            </div>
          ) : null}
        </div>

        {/* Diff strip */}
        <div className="px-4 pt-3">
          <div className="flex flex-wrap items-center gap-1.5 rounded-md bg-slate-50 px-3 py-2 text-[11px]">
            <span className="font-bold text-slate-500 uppercase tracking-wider text-[9px]">Changes</span>
            <span className="text-slate-700">
              <strong className="text-orange-700">{diff.added}</strong> to add
              {" · "}<strong className="text-red-700">{diff.removed}</strong> to drop
            </span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">Tap a cell to toggle</span>
          </div>
        </div>

        {/* Grid */}
        <div className="overflow-x-auto p-4">
          <table className="w-full table-fixed text-center text-[11px]">
            <thead>
              <tr className="font-bold uppercase tracking-wide text-slate-500">
                <th className="w-12 pl-1 text-left"></th>
                {DAY_LABELS.map((d) => <th key={d} className="py-1.5">{d.charAt(0).toUpperCase() + d.slice(1)}</th>)}
              </tr>
            </thead>
            <tbody>
              {BLOCK_LABELS.map((b) => (
                <tr key={b}>
                  <td className="pl-1 text-left text-[10px] font-bold uppercase text-slate-600">
                    {b === "eve" ? "EVE" : b.toUpperCase()}
                  </td>
                  {DAY_LABELS.map((d) => {
                    const was = isCommitted(currentBlocks, d, b);
                    const now = isCommitted(proposed, d, b);
                    return (
                      <td key={d} className="py-0.5">
                        <DiffCell was={was} now={now} onClick={() => toggle(d, b)} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 flex flex-wrap gap-3 text-[9px] font-bold uppercase tracking-wider text-slate-500">
            <Legend swatch="bg-slate-900" label="unchanged on" />
            <Legend swatch="bg-orange-500" label="adding" />
            <Legend swatch="bg-red-500" label="dropping" />
            <Legend swatch="bg-white border border-slate-200" label="off" />
          </div>
        </div>

        {/* Reason picker */}
        <div className="px-4 pb-3">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Reason</label>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {REASONS.map((r) => (
              <button
                key={r}
                onClick={() => setReason(r)}
                className={`rounded-full border px-3 py-1 text-[11px] font-bold transition ${
                  reason === r
                    ? "border-orange-600 bg-orange-500 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {REASON_LABEL[r]}
              </button>
            ))}
          </div>
        </div>

        {/* Note */}
        <div className="px-4 pb-3">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Note <span className="font-normal text-slate-400">(optional)</span></label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={280}
            placeholder="One line of context (kids' school event, doctor's appointment, etc.)"
            className="mt-1.5 w-full resize-none rounded-md border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-orange-500"
          />
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <button
            onClick={onClose}
            className="rounded-lg bg-white border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-100"
          >
            Cancel
          </button>
          <div className="flex items-center gap-2">
            {err ? <span className="text-[11px] text-red-700">{err}</span> : null}
            <button
              onClick={submit}
              disabled={submitting || diff.total === 0 || hasPending || !commitmentId}
              className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-50 hover:bg-orange-600"
            >
              {submitting ? "Sending…" : `Send to my TL/ASM →`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DiffCell({ was, now, onClick }: { was: boolean; now: boolean; onClick: () => void }) {
  // 4 visual states: unchanged-on / adding / dropping / off
  let cls = "h-7 w-12 rounded border-2 text-[10px] font-bold transition ";
  let label = "—";
  if (was && now) {
    cls += "border-slate-900 bg-slate-900 text-white"; label = "✓";
  } else if (!was && now) {
    cls += "border-orange-600 bg-orange-500 text-white"; label = "+";
  } else if (was && !now) {
    cls += "border-red-600 bg-red-500 text-white"; label = "−";
  } else {
    cls += "border-slate-200 bg-white text-slate-400 hover:border-slate-300";
  }
  return <button onClick={onClick} className={`inline-flex items-center justify-center ${cls}`}>{label}</button>;
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      <span className={`inline-block h-2 w-2 rounded-sm ${swatch}`} />
      <span>{label}</span>
    </span>
  );
}
